import { CircularProgress } from "@mui/material";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component"; // Import InfiniteScroll
import { HeadersFunction, LoaderFunction, MetaFunction, redirect, useLoaderData, useRouteError, useSearchParams } from "react-router";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import Chip from "~/components/shared-components/Chip";
import NodataFound from "~/components/shared-components/NoDataFound";
import NoHasMoreText from "~/components/shared-components/NoHasMoreText";
// import VirtuosoMasonryLayout from "~/components/shared-components/VirtuosoMasonryLayout";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { mediaImageDataProps, SatsangMediaItem, SatsangMediaLoaderData } from "~/interface/satsand-media/satsang-media";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { MstPersonType } from "~/type";
import { BLUR_HASH, CACHED_PAGE, FALLBACK_IMAGE_ALT_TEXT, FALLBACK_IMAGE_URL, LANG, SATSANG_MEDIA_PAGE_TITLE } from "~/utils/constant";
import { computeEtagFromData, homeRouteString } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

const VirtuosoMasonryLayout = lazy(() => import("~/components/shared-components/VirtuosoMasonryLayout"));

export const meta: MetaFunction = ({ data, location, params }) => {
  const pathname = location?.pathname || "/";
  const metadata = data as SatsangMediaLoaderData;
  const lang = params.lang || "en";

  // Get SEO data for the specific media type
  const mediaType = metadata?.mediaType;
  const mediaTypeMeta = mediaType ? getPageMeta(mediaType as any, lang) : null;

  // Fallback to satsangMedia if specific media type doesn't have SEO data
  const fallbackMeta = getPageMeta("satsangMedia", lang);
  const meta = mediaTypeMeta || fallbackMeta;

  const category = metadata?.subCategoriesArray ? metadata.subCategoriesArray.charAt(0).toUpperCase() + metadata.subCategoriesArray.slice(1) : "";
  const titleWithCategory = category ? `${metadata?.title} - ${category}` : metadata?.title;

  const baseDescription = meta.metaDescription;
  const descriptionWithCategory = baseDescription;

  const baseTitle = meta.metaTitle;
  const metaTitle = baseTitle;

  return metaService({
    title: metaTitle || meta.metaTitle,
    description: descriptionWithCategory,
    ogTitle: metaTitle || meta.ogTitle,
    ogDescription: descriptionWithCategory,
    ogUrl: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: pathname,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const loader: LoaderFunction = async ({ request, params }) => {
  let mediaType = params.media ? params.media : "mobile-wallpaper";
  // let mediaType = params.media ? params.media : "mobile-wallpaper";

  mediaType = mediaType.replace("-", "_");
  const lang = params.lang;
  const url = new URL(request.url);
  const subCategories = url.searchParams.get("categories");
  const subCategoriesArray = subCategories ? subCategories : null;

  //fetch master data
  const apiService = new ApiService(lang || LANG.english);
  const masterdataResponce = await apiService.getMasterData();
  const mediaCategoriesArray = masterdataResponce[mediaType] || [];

  const subCategoriesId = mediaCategoriesArray.find(
    (item: MstPersonType) => item.text.toLowerCase().trim() === subCategoriesArray?.toLowerCase().trim(),
  );

  //redirect if media type is not in the url
  if (!params.media) {
    return redirect(params.lang === LANG.gujarati ? `/${params.lang}/satsang-media/${mediaType}` : `/satsang-media/${mediaType}`);
  }

  // const title = `${capitalize(mediaType.replace(/_/g, " "))}`;
  const title = SATSANG_MEDIA_PAGE_TITLE[mediaType as keyof typeof SATSANG_MEDIA_PAGE_TITLE]?.[lang === LANG.gujarati ? "gu" : "en"];

  const data = await apiService.getSatsangMediaData(
    {
      pageIndex: 1,
      pageSize: 24,
      subCategory: subCategoriesId?.id || undefined,
    },
    mediaType,
  );

  const totalCount = data?.count || 0;
  const satsangMedia = data?.rows;

  const mediaImageData = satsangMedia.map((item: SatsangMediaItem) => ({
    id: item.id,
    img_data: item.img,
    tag: item.tag,
    name: item.name,
    media_type: item.media_type,
    media_url: item.media_url,
  }));

  const DATA = {
    mediaType,
    satsangMedia,
    title,
    totalCount,
    mediaCategoriesArray,
    subCategoriesArray,
    mediaImageData,
    subCategoriesId,
  };

  const etag = computeEtagFromData(DATA);
  const ifNoneMatch = request.headers.get("If-None-Match");
  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: {
      ...CACHED_PAGE.SATSANG_MEDIA.HEADERS,
      ETag: etag,
    },
  });
};

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
  breadcrumbSchema: (match: any) => [
    { name: "Home", path: "/" },
    { name: match.data?.title || "Satsang Media", path: "/satsang-media" },
  ],
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

function SatsangMedia() {
  const { mediaCategoriesArray, subCategoriesArray, mediaImageData, mediaType, subCategoriesId, totalCount } =
    useLoaderData<SatsangMediaLoaderData>();

  const { t } = useTranslation();

  const [hasServer, setHasServer] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchParamasArray, setSearchParamsArray] = useState<string>(subCategoriesArray);
  const [imagedata, setImageData] = useState<mediaImageDataProps[]>(mediaImageData);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(totalCount === mediaImageData.length ? false : true);
  const lang = useLanguageParams();
  const apiService = new ApiService(lang || LANG.english);

  const handleFetchMoreImages = async () => {
    // Prevent API calls if already loading
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      if (hasMore) {
        const response = await apiService.getSatsangMediaData(
          {
            pageIndex: page + 1,
            pageSize: 24,
            subCategory: subCategoriesId?.id || undefined,
          },
          mediaType,
        );
        const newImageData = response?.rows?.map((item: SatsangMediaItem) => ({
          id: item.id,
          img_data: item.img ? item.img : null,
          tag: item.tag,
          name: item.name,
        }));
        setImageData((prevImages) => [...prevImages, ...newImageData]);
        setPage((prevPage) => prevPage + 1);
        setHasMore(imagedata.length === totalCount ? false : true);
      }
    } catch (error) {
      console.error("Error fetching more images:", error);
      setHasMore(false); // Stop fetching if there's an error
    } finally {
      setLoading(false); // Reset loading state after request finishes
    }
  };

  const handleSelectedType = (type: string) => {
    setPage(1);
    if (searchParamasArray === type) {
      setSearchParams({});
      setSearchParamsArray("");
    } else {
      setSearchParams({ categories: type.toLowerCase() }, { replace: true });
      setSearchParamsArray(type.toLowerCase());
    }
  };

  useEffect(() => {
    setImageData(mediaImageData);
    setPage(1);
    mediaImageData.length === totalCount ? setHasMore(false) : setHasMore(true);
  }, [mediaImageData]);

  useEffect(() => {
    setHasServer(true);
  }, []);

  return (
    <div className="w-full flex flex-col mb-5 scrollbar-hide overflow-hidden">
      <div className="w-full flex justify-center items-center mb-5">
        {mediaCategoriesArray?.length > 0 && (
          <Chip
            chipData={mediaCategoriesArray.map((item) => item.text.toLowerCase())}
            selectedValue={subCategoriesArray}
            onClick={(value) => handleSelectedType(value.toString())}
          />
        )}
      </div>
      {imagedata.length > 0 ? (
        <InfiniteScroll
          key={searchParamasArray}
          dataLength={imagedata.length}
          next={handleFetchMoreImages}
          hasMore={hasMore}
          scrollThreshold={0.4}
          loader={hasServer && <div className="w-full flex justify-center items-center py-14">{<CircularProgress color="inherit" />}</div>}
          endMessage={
            <NoHasMoreText className="text-textSecondaryColor" text={t("common.seeAllData", { data: `${mediaType.replace(/_/g, " ")}` })} />
          }
        >
          <Suspense
            fallback={
              <VirtuosoMasonryLayout
                imageData={imagedata?.map((data) => {
                  return {
                    id: data.id,
                    urls: data.img_data
                      ? {
                          small: data.img_data.s?.url ?? FALLBACK_IMAGE_URL, // Safe access with fallback
                          medium: data.img_data.m?.url ?? FALLBACK_IMAGE_URL,
                          large: data.img_data.l?.url ?? FALLBACK_IMAGE_URL,
                          hd: data.img_data.hd?.url ?? FALLBACK_IMAGE_URL,
                          original: data.img_data.og?.url ?? FALLBACK_IMAGE_URL,
                        }
                      : {
                          small: FALLBACK_IMAGE_URL,
                          medium: FALLBACK_IMAGE_URL,
                          large: FALLBACK_IMAGE_URL,
                          hd: FALLBACK_IMAGE_URL,
                          original: FALLBACK_IMAGE_URL,
                        },
                    width: data.img_data ? (data.img_data.m?.w ?? 500) : 500,
                    height: data.img_data ? (data.img_data.m?.h ?? 500) : 500,
                    blur_hash: data.img_data ? (data.img_data.bh ?? BLUR_HASH) : BLUR_HASH,
                    alt: data?.name || FALLBACK_IMAGE_ALT_TEXT,
                    tag: data.tag,
                    photoName: data.name || `${mediaType.replace(/_/g, " ")}`,
                    media_type: data.media_type as string,
                    video_url: data.media_url || "",
                  };
                })}
              />
            }
          >
            <VirtuosoMasonryLayout
              imageData={imagedata?.map((data) => {
                return {
                  id: data.id,
                  urls: data.img_data
                    ? {
                        small: data.img_data.s?.url ?? FALLBACK_IMAGE_URL, // Safe access with fallback
                        medium: data.img_data.m?.url ?? FALLBACK_IMAGE_URL,
                        large: data.img_data.l?.url ?? FALLBACK_IMAGE_URL,
                        hd: data.img_data.hd?.url ?? FALLBACK_IMAGE_URL,
                        original: data.img_data.og?.url ?? FALLBACK_IMAGE_URL,
                      }
                    : {
                        small: FALLBACK_IMAGE_URL,
                        medium: FALLBACK_IMAGE_URL,
                        large: FALLBACK_IMAGE_URL,
                        hd: FALLBACK_IMAGE_URL,
                        original: FALLBACK_IMAGE_URL,
                      },
                  width: data.img_data ? (data.img_data.m?.w ?? 500) : 500,
                  height: data.img_data ? (data.img_data.m?.h ?? 500) : 500,
                  blur_hash: data.img_data ? (data.img_data.bh ?? BLUR_HASH) : BLUR_HASH,
                  alt: data?.name || FALLBACK_IMAGE_ALT_TEXT,
                  tag: data.tag,
                  photoName: data.name || `${mediaType.replace(/_/g, " ")}`,
                  media_type: data.media_type,
                  video_url: data.media_url || "",
                };
              })}
            />
          </Suspense>
        </InfiniteScroll>
      ) : (
        <div className="w-full flex justify-center items-center">
          <NodataFound text={t("common.noDataFound")} />
        </div>
      )}
    </div>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  const { t } = useTranslation();
  return (
    <div className="w-full flex flex-col items-center justify-center p-3">
      <NodataFound text={t("common.noDataFound")} />
    </div>
  );
};

export default SatsangMedia;
